import React, { useEffect, useState } from "react";
import ShiftForm from "./ShiftForm";
import CompletedJobs from "./CompletedJobs";
import DriverContactForm from "./DriverContactForm";
import ShiftSummary from "./ShiftSummary";
import EmailReportForm from "./EmailReportForm";
import DriverSummary from "./DriverSummary";
import BreakTracker, { BreakEntry } from "./BreakTracker";
import JobUploadSection from "./JobUploadSection";
import { Job } from "./JobEntry";
import { Button } from "@/components/ui/button";
import AnimatedTabBar, { type AnimatedTabItem } from "@/components/ui/animated-tab-bar";
import { LogOut, Mail, Clock3, CalendarDays, ClipboardCheck, Fuel, Truck, Car, Building2, Upload, CheckCircle2, BarChart3, AlertTriangle, Siren, Bell, MessageSquare } from "lucide-react";
import CommsBook from "@/components/CommsBook";
import { isDispatchOrAdmin as isDispatchOrAdminCheck, countUnreadCommsMessages } from "@/lib/commsBook";
import { getOpenJobsCount } from "@/lib/driverNotifications";
import DriverNotifications from "./DriverNotifications";
import { useAppContext } from "@/contexts/AppContext";
import DriverReporting from "./DriverReporting";
import VehicleInspection from "@/pages/VehicleInspection";
import DriverFuelCard from "@/pages/DriverFuelCard";
import DriverRosterView from "@/pages/DriverRosterView";
import ReportIncident from "@/pages/ReportIncident";
import DriverFleetSlotsPanel from "@/components/roster/DriverFleetSlotsPanel";
import { useDriverFleetWindow } from "@/components/roster/useDriverFleetWindow";
import { useDriverVehicleCheckStatus } from "@/hooks/useDriverVehicleCheckStatus";
import { loadVehicleTypes, getIconStyle, type VehicleTypeRow } from "@/lib/vehicleTypes";
import { STS_VEHICLE_TYPE } from "@/components/fleet-capacity/utils";


// 0/3 the day of a check, 1/3 the next day... capped at intervalDays for
// display once overdue, since the countdown itself is meant to read as "how
// close am I", not keep climbing past the point that matters. Colour comes
// straight from the two numbers rather than the fleet_inspection_green/
// amber/red_*_days settings -- those default to values (e.g. 4 days) that
// don't line up with a 3-day interval and aren't consumed anywhere else
// today, so reusing them would just be two settings admins have to keep in
// sync for no real benefit.
const VehicleCheckCountdownBadge: React.FC<{
  daysSinceLastCheck: number | null;
  intervalDays: number;
}> = ({ daysSinceLastCheck, intervalDays }) => {
  if (daysSinceLastCheck === null) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-950 text-red-300 border border-red-800 animate-work-time-breach">
        <AlertTriangle className="w-3.5 h-3.5" />
        Vehicle Check: none on file
      </span>
    );
  }

  const displayDays = Math.min(daysSinceLastCheck, intervalDays);
  const isOverdue = daysSinceLastCheck >= intervalDays;
  const colourCls = isOverdue
    ? "bg-red-950 text-red-300 border-red-800 animate-work-time-breach"
    : displayDays === 0
    ? "bg-green-950 text-green-300 border-green-800"
    : "bg-amber-950 text-amber-300 border-amber-800";

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${colourCls}`}>
      {isOverdue && <AlertTriangle className="w-3.5 h-3.5" />}
      Vehicle Check: {displayDays}/{intervalDays} days
    </span>
  );
};

interface ShiftData {
  id?: string;
  startTime: string;
  startOdo: number;
  endTime?: string;
  endOdo?: number;
  breakTime?: number;
  breaks?: BreakEntry[];
  shiftDate?: string;
  vehicleId?: string;
}

interface DriverDashboardProps {
  driverName: string;
  vehicleId?: string;
  onLogout: () => void;
  isImpersonating?: boolean;
}

const DriverDashboard: React.FC<DriverDashboardProps> = ({
  driverName,
  vehicleId,
  onLogout,
  isImpersonating = false,
}) => {
  const { drivers, addTimesheet } = useAppContext();
  const [currentShift, setCurrentShift] = useState<ShiftData | null>(null);
  const [shifts, setShifts] = useState<ShiftData[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showShiftSummary, setShowShiftSummary] = useState(false);
  const [showEmailReport, setShowEmailReport] = useState(false);
  const [showDriverSummary, setShowDriverSummary] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>(
    vehicleId || ""
  );
  const [activeTab, setActiveTab] = useState("notifications");

  // TMV/Car-SUV/STS Slots only show up as their own tab when this driver
  // actually has a real driving shift in that segment coming up (today/
  // tomorrow/day-after) -- same gating TMV Slots used to do nested inside
  // "My Roster", just relocated to the top level and extended to the other
  // two fleet segments.
  const { anyTmv: hasTmvShiftSoon, anyCar: hasCarShiftSoon, anySts: hasStsShiftSoon } = useDriverFleetWindow(driverName);

  const [allVehicleTypes, setAllVehicleTypes] = useState<VehicleTypeRow[]>([]);
  useEffect(() => {
    loadVehicleTypes().then(setAllVehicleTypes);
  }, []);
  const tmvVehicleTypes = allVehicleTypes.filter((t) => getIconStyle(t.vehicle_type, allVehicleTypes) === "truck");
  const carVehicleTypes = allVehicleTypes.filter((t) => getIconStyle(t.vehicle_type, allVehicleTypes) === "car");

  // Prefer driver_name; fall back to name just in case some pages still use it
  const currentDriver =
    drivers.find((d: any) => d.driver_name === driverName) ||
    drivers.find((d: any) => d.name === driverName);

  // Comms Book / Uncomplete Jobs tab badges -- unread message count and
  // open job count, matching exactly what each tab itself shows (see
  // countUnreadCommsMessages / getOpenJobsCount). Refetched on driver
  // change, on every tab switch (covers "just read/closed something in
  // that tab"), and on a light interval so the badge doesn't go stale
  // while sitting elsewhere.
  const [unreadCommsCount, setUnreadCommsCount] = useState(0);
  const [openJobsCount, setOpenJobsCount] = useState(0);
  useEffect(() => {
    if (!driverName) return;
    let cancelled = false;
    const refresh = async () => {
      const [unread, openJobs] = await Promise.all([
        countUnreadCommsMessages(driverName, isDispatchOrAdminCheck(currentDriver), currentDriver?.roster_groups),
        getOpenJobsCount(driverName),
      ]);
      if (!cancelled) {
        setUnreadCommsCount(unread);
        setOpenJobsCount(openJobs);
      }
    };
    refresh();
    const interval = setInterval(refresh, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverName, activeTab]);

  // Vehicle-check countdown/lockout -- driver-scoped (any vehicle this
  // driver last checked, not today's rostered vehicle specifically), see
  // useDriverVehicleCheckStatus.ts. Days-only: once the check is overdue,
  // every tab except Vehicle Check gets blocked below. Exempt admins (see
  // exempt_from_inspection on the driver's profile) never get locked out.
  const checkStatus = useDriverVehicleCheckStatus(driverName, !!currentDriver?.exempt_from_inspection);

  // Admins previewing a driver via the View Emulator (isImpersonating) can
  // see the real lockout state, then choose to look past it -- per Janelle,
  // it should still show as locked out first, not be silently bypassed.
  const [lockoutOverridden, setLockoutOverridden] = useState(false);
  const effectivelyLockedOut = checkStatus.isLockedOut && !(isImpersonating && lockoutOverridden);

  const allTabs: AnimatedTabItem[] = [
    { value: "notifications", label: "Notifications", icon: Bell },
    { value: "comms-book", label: "Comms Book", icon: MessageSquare, badge: unreadCommsCount },
    { value: "shift", label: "Shift Mgmt", icon: Clock3 },
    { value: "roster", label: "My Roster", icon: CalendarDays },
    { value: "vehicle-check", label: "Vehicle Check", icon: ClipboardCheck },
    { value: "fuel-card", label: "Fuel Card", icon: Fuel },
    ...(hasTmvShiftSoon ? [{ value: "tmv-slots", label: "TMV Slots", icon: Truck }] : []),
    ...(hasCarShiftSoon ? [{ value: "car-slots", label: "Car/SUV Slots", icon: Car }] : []),
    ...(hasStsShiftSoon ? [{ value: "sts-slots", label: "STS Slots", icon: Building2 }] : []),
    { value: "report-incident", label: "Report Incident", icon: Siren },
    { value: "upload", label: "Uncomplete Jobs", icon: Upload, badge: openJobsCount },
    { value: "completed", label: "Completed", icon: CheckCircle2 },
    { value: "reporting", label: "Reporting", icon: BarChart3 },
  ];
  const tabs = effectivelyLockedOut ? allTabs.filter((t) => t.value === "vehicle-check") : allTabs;

  // Mobile-only 2-row split: daily-use tabs on the bottom row (closest to
  // the thumb), everything else on the row above. Desktop stays a single
  // full-width row -- there's room for all of them there. "Primary" is a
  // whitelist and "secondary" is just whatever's left, so this still works
  // correctly during a vehicle-check lockout (tabs is reduced to just that
  // one tab, which lands in secondary with nothing in primary).
  const primaryTabValues = new Set(["notifications", "shift", "roster", "upload", "completed"]);
  const primaryTabs = tabs.filter((t) => primaryTabValues.has(t.value));
  const secondaryTabs = tabs.filter((t) => !primaryTabValues.has(t.value));

  // Force onto Vehicle Check the moment a lockout kicks in (including if it
  // flips true mid-session, e.g. a new day rolling over) -- not on every
  // render, just when the locked state actually changes.
  useEffect(() => {
    if (effectivelyLockedOut && activeTab !== "vehicle-check") {
      setActiveTab("vehicle-check");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectivelyLockedOut]);

  const needsContactInfo =
    currentDriver && (!currentDriver.email || !currentDriver.phone);

  const handleShiftUpdate = (shiftData: ShiftData) => {
    const updatedShift = { ...shiftData, vehicleId: selectedVehicleId };
    setCurrentShift(updatedShift);

    if (shiftData.endTime && shiftData.endOdo && currentDriver) {
      const totalKm = shiftData.endOdo - shiftData.startOdo;
      const totalEarnings = jobs.reduce((sum, job) => sum + job.totalFare, 0);

      addTimesheet({
        driverId: currentDriver.id,
        date: shiftData.shiftDate ? new Date(shiftData.shiftDate) : new Date(),
        loginTime: shiftData.startTime,
        logoffTime: shiftData.endTime,
        totalHours: calculateWorkHours(shiftData.startTime, shiftData.endTime),
        breaks: (shiftData.breakTime || 0) / 60,
        breakEntries: shiftData.breaks,
        startKM: shiftData.startOdo,
        finishKM: shiftData.endOdo,
        totalKM: totalKm,
        totalEarnings,
        vehicleId: selectedVehicleId,
      });

      setShifts((prev) => {
        const existing = prev.find((s) => s.startTime === shiftData.startTime);
        if (existing) {
          return prev.map((s) =>
            s.startTime === shiftData.startTime ? updatedShift : s
          );
        }
        return [...prev, updatedShift];
      });
    }
  };

  const calculateWorkHours = (startTime: string, endTime: string): number => {
    const start = new Date(`2024-01-01T${startTime}`);
    const end = new Date(`2024-01-01T${endTime}`);
    return (end.getTime() - start.getTime()) / (1000 * 60 * 60);
  };

  const handleBreakUpdate = (breaks: BreakEntry[]) => {
    if (currentShift) {
      const totalBreakTime = breaks.reduce(
        (total, b) => total + (b.duration || 0),
        0
      );
      const updatedShift = { ...currentShift, breakTime: totalBreakTime, breaks };
      setCurrentShift(updatedShift);
    }
  };

  const handleContactSave = () => setShowContactForm(false);

  if (needsContactInfo && !showContactForm && !isImpersonating) {
    return <DriverContactForm driver={currentDriver} onSave={handleContactSave} />;
  }

  if (showContactForm && currentDriver) {
    return <DriverContactForm driver={currentDriver} onSave={handleContactSave} />;
  }

  return (
    <div className="min-h-screen p-2 sm:p-4">
      <div className="max-w-6xl mx-auto">
        <div className="bg-card border border-border rounded-lg shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold golden-text">Driver Dashboard</h1>
              <p className="text-muted-foreground">Welcome, {driverName}</p>
              {currentDriver?.email && (
                <p className="text-sm text-muted-foreground">Email: {currentDriver.email}</p>
              )}
              {checkStatus.worst && (
                <div className="mt-2">
                  <VehicleCheckCountdownBadge
                    daysSinceLastCheck={checkStatus.worst.daysSinceLastCheck}
                    intervalDays={checkStatus.worst.intervalDays}
                  />
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => setShowEmailReport(!showEmailReport)}
                variant="outline"
                size="sm"
                className="border-yellow-600 text-yellow-400 hover:bg-yellow-900"
              >
                <Mail className="w-4 h-4 mr-1" />
                Email Report
              </Button>
              <Button
                onClick={() => setShowShiftSummary(!showShiftSummary)}
                variant="outline"
                size="sm"
                className="border-yellow-600 text-yellow-400 hover:bg-yellow-900"
              >
                Shift
              </Button>
              <Button
                onClick={() => setShowDriverSummary(!showDriverSummary)}
                variant="outline"
                size="sm"
                className="border-yellow-600 text-yellow-400 hover:bg-yellow-900"
              >
                Period
              </Button>
              <Button
                onClick={() => setShowContactForm(true)}
                variant="outline"
                size="sm"
                className="border-yellow-600 text-yellow-400 hover:bg-yellow-900"
              >
                Contact
              </Button>
              {/* Hide logout button when impersonating - admin uses the banner to exit */}
              {!isImpersonating && (
                <Button
                  onClick={onLogout}
                  variant="outline"
                  size="sm"
                  className="border-red-600 text-red-400 hover:bg-red-900"
                >
                  <LogOut className="w-4 h-4 mr-1" />
                  Logout
                </Button>
              )}
            </div>
          </div>
        </div>

        {showEmailReport && (
          <div className="mb-6">
            <EmailReportForm
              reportData={{ shifts, jobs }}
              reportType="Driver Shift"
              onClose={() => setShowEmailReport(false)}
            />
          </div>
        )}

        {showShiftSummary && (
          <div className="mb-6">
            <ShiftSummary
              driverId={currentDriver?.id}
              onClose={() => setShowShiftSummary(false)}
            />
          </div>
        )}

        {showDriverSummary && (
          <div className="mb-6">
            <DriverSummary
              driverId={currentDriver?.id}
              startDate={new Date()}
              endDate={new Date()}
              onClose={() => setShowDriverSummary(false)}
            />
          </div>
        )}

        {checkStatus.isLockedOut && (
          <div className="mb-4 p-4 rounded-lg border border-red-800 bg-red-950/60 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-red-200">{checkStatus.lockoutMessage}</p>
              {isImpersonating && !lockoutOverridden && (
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3 border-red-700 text-red-200 hover:bg-red-900/40"
                  onClick={() => setLockoutOverridden(true)}
                >
                  Admin Override — View Anyway
                </Button>
              )}
              {isImpersonating && lockoutOverridden && (
                <p className="text-xs text-red-300/80 mt-2 italic">
                  Lockout overridden for this preview — the driver still sees the real lockout.
                </p>
              )}
            </div>
          </div>
        )}

        <div className="bg-card border border-border rounded-lg shadow-lg p-2 sm:p-4 mb-4">
          {/* Desktop/tablet: everything fits, single full-width bar. */}
          <div className="hidden sm:block">
            <AnimatedTabBar tabs={tabs} value={activeTab} onValueChange={setActiveTab} className="mb-4" />
          </div>

          {/* Mobile: split into 2 rows so nothing gets cramped -- daily-use
              tabs on the bottom row (closest to the thumb), the rest above. */}
          <div className="sm:hidden animated-tab-bar-stack mb-4">
            {secondaryTabs.length > 0 && (
              <AnimatedTabBar tabs={secondaryTabs} value={activeTab} onValueChange={setActiveTab} reorderActiveToFront />
            )}
            {primaryTabs.length > 0 && (
              <AnimatedTabBar
                tabs={primaryTabs}
                value={activeTab}
                onValueChange={setActiveTab}
                reorderActiveToFront
                wavePosition="down"
                labelPosition="top"
              />
            )}
          </div>

          {activeTab === "notifications" && <DriverNotifications driverName={driverName} />}

          {activeTab === "comms-book" && (
            <CommsBook
              currentDriverName={driverName}
              isDispatchOrAdmin={isDispatchOrAdminCheck(currentDriver)}
              currentDriverRosterGroups={currentDriver?.roster_groups}
            />
          )}

          {activeTab === "roster" && (
            <div className="space-y-4">
              <DriverRosterView driverName={driverName} />
            </div>
          )}

          {activeTab === "shift" && (
            <div className="space-y-4">
              <ShiftForm
                driverName={driverName}
                onShiftUpdate={handleShiftUpdate}
                currentShift={currentShift}
                selectedVehicleId={selectedVehicleId}
                onVehicleChange={setSelectedVehicleId}
                driverId={currentDriver?.id}
              />
              {currentShift?.startTime && !currentShift?.endTime && (
                <BreakTracker onBreakUpdate={handleBreakUpdate} />
              )}
            </div>
          )}

          {activeTab === "vehicle-check" && (
            <VehicleInspection driverName={driverName} onInspectionSubmitted={checkStatus.refetch} />
          )}

          {activeTab === "fuel-card" && <DriverFuelCard driverName={driverName} />}

          {activeTab === "tmv-slots" && hasTmvShiftSoon && (
            <DriverFleetSlotsPanel
              vehicleTypes={tmvVehicleTypes}
              title="TMV Slots"
              icon={Truck}
              iconColorClass="text-cyan-400"
              accentColorClass="bg-cyan-600 border-cyan-500"
              emptyMessage="No TMV vehicles rostered this day."
            />
          )}

          {activeTab === "car-slots" && hasCarShiftSoon && (
            <DriverFleetSlotsPanel
              vehicleTypes={carVehicleTypes}
              title="Car/SUV Slots"
              icon={Car}
              iconColorClass="text-blue-400"
              accentColorClass="bg-blue-600 border-blue-500"
              emptyMessage="No Car/SUV vehicles rostered this day."
            />
          )}

          {activeTab === "sts-slots" && hasStsShiftSoon && (
            <DriverFleetSlotsPanel
              vehicleTypes={[STS_VEHICLE_TYPE]}
              title="STS Slots"
              icon={Building2}
              iconColorClass="text-sky-400"
              accentColorClass="bg-sky-600 border-sky-500"
              emptyMessage="No STS work rostered this day."
            />
          )}

          {activeTab === "report-incident" && <ReportIncident driverName={driverName} />}

          {activeTab === "upload" && <JobUploadSection driverName={driverName} />}

          {activeTab === "completed" && (
            <CompletedJobs
              driverName={driverName}
              currentShiftOnly={true}
              showValidation={false}
            />
          )}

          {/* Driver Reporting — Weekly / Full / PDF Summaries sub-tabs */}
          {activeTab === "reporting" && (
            <div className="space-y-4">
              <DriverReporting driverName={driverName} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverDashboard;
